import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Bookmark,
  Check,
  CircleAlert,
  Plus,
  Save,
  Sparkles,
  Trash2,
  UserCircle,
  X as XIcon,
  KeyRound,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api, ApiError, type UserApp } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'
import { buildBookmarklet } from '@/lib/bookmarklet'

export default function Settings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">Account, X connection, voice, apps, bookmarklet.</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="x">X connection</TabsTrigger>
          <TabsTrigger value="voice">Voice</TabsTrigger>
          <TabsTrigger value="ai">AI</TabsTrigger>
          <TabsTrigger value="apps">My apps</TabsTrigger>
          <TabsTrigger value="bookmarklet">Bookmarklet</TabsTrigger>
        </TabsList>
        <TabsContent value="general"><GeneralPanel /></TabsContent>
        <TabsContent value="x"><XPanel /></TabsContent>
        <TabsContent value="voice"><VoicePanel /></TabsContent>
        <TabsContent value="ai"><AiPanel /></TabsContent>
        <TabsContent value="apps"><AppsPanel /></TabsContent>
        <TabsContent value="bookmarklet"><BookmarkletPanel /></TabsContent>
      </Tabs>
    </div>
  )
}

function GeneralPanel() {
  const { me } = useAuth()
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Account</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 text-sm">
          <UserCircle className="h-5 w-5 text-muted-foreground" />
          <div>
            <div>{me?.email}</div>
            <div className="text-xs text-muted-foreground">Plan: {me?.plan}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function XPanel() {
  const { me } = useAuth()
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">X account</CardTitle>
        <CardDescription>Bluebird posts through this account. Sign out from the sidebar to switch accounts.</CardDescription>
      </CardHeader>
      <CardContent>
        {me?.x.connected ? (
          <div className="rounded-md border bg-emerald-50 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="font-medium">Signed in as @{me.x.handle}</div>
                <div className="text-xs text-muted-foreground">All posts and replies fire through this account.</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border bg-amber-50 px-4 py-3 text-sm dark:border-amber-900/40 dark:bg-amber-950/30">
            <div className="flex items-center gap-2">
              <CircleAlert className="h-4 w-4 text-amber-600" />
              <span>Your X tokens were cleared. Sign out and sign back in with X.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function VoicePanel() {
  const { me, refresh } = useAuth()
  const [voice, setVoice] = useState(me?.voiceSample ?? '')
  const [displayName, setDisplayName] = useState(me?.displayName ?? '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setVoice(me?.voiceSample ?? '')
    setDisplayName(me?.displayName ?? '')
  }, [me])

  async function save() {
    setBusy(true)
    try {
      await api.updateMe({ voiceSample: voice, displayName: displayName || null })
      toast.success('Saved')
      refresh()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Voice</CardTitle>
        <CardDescription>
          Paste 3–8 of your tweets that sound like "you." Claude will match this tone when drafting threads and replies.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="name" className="text-xs">Display name</Label>
          <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jared" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="voice" className="text-xs">Voice samples</Label>
          <Textarea
            id="voice"
            rows={10}
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            placeholder="Paste a few tweets. Blank lines between them are fine."
          />
          <p className="text-[11px] text-muted-foreground">Optional but dramatically improves drafts. Max 5000 chars.</p>
        </div>
        <Button onClick={save} disabled={busy}>
          <Save className="h-4 w-4" />
          Save
        </Button>
      </CardContent>
    </Card>
  )
}

function AiPanel() {
  const { me, refresh } = useAuth()
  const ai = me?.ai
  const [keyInput, setKeyInput] = useState('')
  const [model, setModel] = useState<string>(ai?.modelPreference ?? '')
  const [busyKey, setBusyKey] = useState(false)
  const [busyModel, setBusyModel] = useState(false)

  useEffect(() => { setModel(ai?.modelPreference ?? '') }, [ai?.modelPreference])

  async function saveKey() {
    if (!keyInput.trim()) return
    setBusyKey(true)
    try {
      await api.updateAi({ apiKey: keyInput.trim() })
      toast.success('Anthropic key saved')
      setKeyInput('')
      refresh()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusyKey(false)
    }
  }

  async function clearKey() {
    if (!confirm('Stop using your own Anthropic key? Future runs will use the default.')) return
    setBusyKey(true)
    try {
      await api.updateAi({ apiKey: null })
      toast.success('Reverted to default key')
      refresh()
    } finally {
      setBusyKey(false)
    }
  }

  async function saveModel(next: string) {
    setBusyModel(true)
    setModel(next)
    try {
      await api.updateAi({ modelPreference: next || null })
      toast.success('Model preference saved')
      refresh()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusyModel(false)
    }
  }

  if (!ai) return null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Anthropic API key</CardTitle>
          <CardDescription>
            Optional. If you provide your own, all of your Claude calls bill to your Anthropic account
            instead of ours. The key is encrypted at rest and never returned to the browser after saving.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            Currently using:{' '}
            <strong>
              {ai.keySource === 'user' ? 'your key' : 'default (system) key'}
            </strong>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="ak" className="text-xs">
                {ai.hasOwnKey ? 'Replace key' : 'Add key'}
              </Label>
              <Input
                id="ak"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="sk-ant-..."
                autoComplete="off"
              />
            </div>
            <Button onClick={saveKey} disabled={busyKey || !keyInput.trim()}>
              <KeyRound className="h-4 w-4" />
              Save
            </Button>
            {ai.hasOwnKey && (
              <Button variant="ghost" onClick={clearKey} disabled={busyKey}>
                <XIcon className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Get a key at <a href="https://console.anthropic.com/" target="_blank" rel="noreferrer" className="text-primary hover:underline">console.anthropic.com</a>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model</CardTitle>
          <CardDescription>
            Used for thread generation and reply drafting. Defaults to{' '}
            <strong>Haiku</strong> on Free and <strong>Sonnet</strong> on Pro. Add your own
            Anthropic key above to unlock model selection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            Currently using: <strong>{ai.effectiveModel}</strong>{' '}
            <span className="text-xs text-muted-foreground">
              ({ai.modelSource === 'user' ? 'your choice' : `default for ${me?.plan}`})
            </span>
          </div>
          <div className="space-y-1">
            <Label htmlFor="model" className="text-xs">Model preference</Label>
            <select
              id="model"
              value={model}
              onChange={(e) => saveModel(e.target.value)}
              disabled={busyModel || !ai.hasOwnKey}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">Use default for my plan</option>
              {ai.availableModels.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            {ai.hasOwnKey ? (
              <p className="text-[11px] text-muted-foreground">
                Heavier models cost more on your Anthropic account but produce better-sounding drafts.
                <Sparkles className="ml-1 inline h-3 w-3" />
              </p>
            ) : (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                Locked while using the default key. Add your own Anthropic key above to choose any model.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function AppsPanel() {
  const [apps, setApps] = useState<UserApp[]>([])
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [positioning, setPositioning] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    setApps(await api.listApps())
  }
  useEffect(() => { load() }, [])

  async function add() {
    setBusy(true)
    try {
      await api.createApp({ name, url, positioning: positioning || undefined })
      setName(''); setUrl(''); setPositioning('')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add')
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    if (!confirm('Remove this app?')) return
    await api.deleteApp(id)
    load()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">My apps</CardTitle>
        <CardDescription>
          List the apps you want to promote. Claude will weave a mention into a reply <strong>only when genuinely
          relevant</strong> — at most one of the three variants, never forced. Positioning helps Claude decide when
          it's relevant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="app-name" className="text-xs">App name</Label>
            <Input id="app-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your app" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="app-url" className="text-xs">URL</Label>
            <Input id="app-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourapp.com" />
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="app-pos" className="text-xs">Positioning</Label>
            <span className={`text-[11px] ${positioning.length > 2000 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {positioning.length}/2000
            </span>
          </div>
          <Textarea
            id="app-pos"
            rows={4}
            value={positioning}
            onChange={(e) => setPositioning(e.target.value)}
            placeholder="What it does, who it's for, and when it's worth mentioning. The more specific the trigger, the fewer off-base mentions Claude will generate."
          />
        </div>
        <Button onClick={add} disabled={busy || !name.trim() || !url.trim() || positioning.length > 2000}>
          <Plus className="h-4 w-4" />
          Add app
        </Button>

        <div className="space-y-1.5 pt-2">
          {apps.length === 0 && (
            <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              No apps yet. Add one to let Claude mention it in replies when contextually relevant.
            </p>
          )}
          {apps.map((a) => (
            <div key={a.id} className="rounded-md border bg-background px-3 py-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{a.name}</span>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                      {a.url.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                  {a.positioning && <p className="mt-1 text-sm text-muted-foreground">{a.positioning}</p>}
                </div>
                <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function BookmarkletPanel() {
  const apiBase = window.location.origin
  const href = buildBookmarklet({ apiBase })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Install the bookmarklet</CardTitle>
        <CardDescription>
          One click on any x.com page — your home feed, a search, a profile, a single tweet —
          scrapes the visible tweets and pipes them into Bluebird for classification and drafting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="ml-4 list-decimal space-y-1.5 text-sm text-muted-foreground">
          <li>Drag the <strong>Add to Bluebird</strong> button below to your browser's bookmarks bar.</li>
          <li>Make sure you're signed into Bluebird in any tab (session cookie needs to be live).</li>
          <li>Visit x.com. Click the bookmark on any tweet or feed — a new tab opens to Bluebird and the scrape starts.</li>
        </ol>

        <div className="rounded-md border bg-muted/30 p-4 text-center">
          <a
            href={href}
            draggable
            onClick={(e) => e.preventDefault()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
          >
            <Bookmark className="h-4 w-4" />
            Add to Bluebird
          </a>
          <p className="mt-2 text-xs text-muted-foreground">Drag this button onto your bookmarks bar.</p>
        </div>

        <details className="rounded-md border bg-background p-3 text-xs">
          <summary className="cursor-pointer font-medium">How it works under the hood</summary>
          <p className="mt-2 text-muted-foreground">
            X's Content-Security-Policy blocks cross-origin fetch, so the bookmarklet can't POST directly
            to our API. Instead it encodes the scraped tweets into the URL fragment and opens a new tab
            to <code>bluebirdie.win/app/bookmarklet/ingest</code>, which posts to our API from our own
            origin using your session cookie. No personal access token required.
          </p>
        </details>
      </CardContent>
    </Card>
  )
}
